import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useAnimation } from "framer-motion";
import {
  Compass,
  Map,
  Ruler,
  Building2,
  ArrowRight,
  CheckCircle2,
  Users,
  Award,
  Clock,
  Target,
  Shield,
  Lightbulb,
} from "lucide-react";

// Animation component
function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.6, delay, ease: "easeOut" },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Hero Section
function Hero() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-white pt-16">
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(#032125 1px, transparent 1px),
              linear-gradient(90deg, #032125 1px, transparent 1px)
            `,
            backgroundSize: "60px 60px",
          }}
        />
        <motion.div
          className="absolute top-20 right-20 w-[500px] h-[500px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(219,106,64,0.15) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-[400px] h-[400px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(3,33,37,0.1) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <svg
          className="absolute inset-0 w-full h-full opacity-20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.line
            x1="70%"
            y1="20%"
            x2="85%"
            y2="40%"
            stroke="#DB6A40"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
          <motion.line
            x1="75%"
            y1="30%"
            x2="60%"
            y2="50%"
            stroke="#DB6A40"
            strokeWidth="1.5"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              repeatType: "reverse",
              delay: 0.3,
            }}
          />
          <motion.line
            x1="80%"
            y1="60%"
            x2="65%"
            y2="70%"
            stroke="#032125"
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatType: "reverse",
              delay: 0.6,
            }}
          />
          <motion.circle
            cx="70%"
            cy="20%"
            r="4"
            fill="#DB6A40"
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <motion.circle
            cx="85%"
            cy="40%"
            r="3"
            fill="#DB6A40"
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
          />
          <motion.circle
            cx="60%"
            cy="50%"
            r="3"
            fill="#032125"
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
          />
        </svg>
      </div>

      <div className="relative z-10 w-full section-padding max-w-7xl mx-auto pt-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <Compass className="w-4 h-4" />
              Professional Land Surveyors
            </motion.div>

            <h1 className="heading-xl text-primary mb-6">
              Committed to quality,{" "}
              <span className="text-gradient block">Committed to you.</span>
            </h1>

            <p className="text-lg text-neutral-600 mb-8 leading-relaxed max-w-lg">
              Bomtrack Nigeria Limited delivers accurate, reliable, and timely
              land surveying services. We transform complex land data into
              clear, actionable insights for property owners, developers, and
              government agencies.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/services"
                className="btn-primary inline-flex items-center gap-2"
              >
                Explore Services
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="btn-outline">
                Contact Us
              </Link>
            </div>

            <div className="flex gap-8 mt-12 pt-8 border-t border-neutral-200">
              {[
                { value: "08+", label: "Years Experience" },
                { value: "300+", label: "Projects Completed" },
                { value: "98%", label: "Client Satisfaction" },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                >
                  <div className="text-2xl sm:text-3xl font-bold text-primary">
                    {stat.value}
                  </div>
                  <div className="text-sm text-neutral-500">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&auto=format&fit=crop&q=80"
                  alt="Land Surveying"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent rounded-2xl" />

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <div className="font-semibold text-primary">
                      Licensed & Certified
                    </div>
                    <div className="text-sm text-neutral-500">
                      By SURCON
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// About Section
function AboutSection() {
  return (
    <section className="py-20 bg-white">
      <div className="section-padding max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <AnimatedSection className="relative">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&auto=format&fit=crop&q=80"
                  alt="Surveying Team"
                  className="w-full h-[450px] object-cover"
                />
              </div>
              <div className="absolute -right-4 top-10 bg-accent text-white rounded-xl p-6 shadow-xl">
                <div className="text-4xl font-bold">08+</div>
                <div className="text-sm opacity-90">
                  Years of
                  <br />
                  Excellence
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <span className="text-accent font-medium">About Us</span>
            <h2 className="heading-md text-primary mt-2 mb-6">
              Committed to Excellence in Every Survey
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-6">
              Bomtrack Nigeria Limited is a leading land surveying company
              dedicated to providing precise and comprehensive surveying
              services across Nigeria. Our team of experienced surveyors
              combines traditional expertise with modern technology to deliver
              accurate results for every project.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-8">
              We pride ourselves on our commitment to quality, integrity, and
              client satisfaction. Whether you need boundary surveys,
              topographic mapping, or construction staking, we ensure every
              detail is captured with precision.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                "Licensed & Certified",
                "Modern Equipment",
                "Timely Delivery",
                "Competitive Pricing",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0" />
                  <span className="text-neutral-700">{item}</span>
                </div>
              ))}
            </div>

            <Link
              to="/about"
              className="btn-secondary inline-flex items-center gap-2"
            >
              Learn More About Us
              <ArrowRight className="w-4 h-4" />
            </Link>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

// Services Section
function ServicesSection() {
  const services = [
    {
      icon: Map,
      title: "Topographic Survey",
      description:
        "Detailed mapping of natural and man-made features including elevation, contours, and existing structures.",
      image:
        "/services/topographic-survey.jpg",
    },
    {
      icon: Ruler,
      title: "Boundary Survey",
      description:
        "Accurate determination of property lines and corners to resolve disputes and establish clear boundaries.",
      image:
        "/services/boundary-survey.jpg",
    },
    {
      icon: Building2,
      title: "Construction Survey",
      description:
        "Precision staking and layout services for construction projects ensuring buildings are built exactly as designed.",
      image:
        "/services/construction-survey.jpg",
    },
  ];

  return (
    <section className="py-20 bg-neutral-50">
      <div className="section-padding max-w-7xl mx-auto">
        <AnimatedSection className="text-center mb-16">
          <span className="text-accent font-medium">Our Services</span>
          <h2 className="heading-md text-primary mt-2 mb-4">
            Comprehensive Surveying Solutions
          </h2>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            We offer a full range of professional land surveying services
            tailored to meet your specific needs, from residential to commercial
            projects.
          </p>
        </AnimatedSection>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <AnimatedSection key={service.title} delay={index * 0.1}>
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg card-hover group">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-primary mb-3">
                    {service.title}
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 text-accent font-medium hover:gap-3 transition-all"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection delay={0.4} className="text-center mt-12">
          <Link
            to="/services"
            className="btn-primary inline-flex items-center gap-2"
          >
            View All Services
            <ArrowRight className="w-4 h-4" />
          </Link>
        </AnimatedSection>
      </div>
    </section>
  );
}

// Why choose us Section
function FeaturesSection() {
  const features = [
    {
      icon: Target,
      title: "Precision & Accuracy",
      description:
        "We use state-of-the-art equipment and proven methodologies to ensure the highest level of accuracy in every survey.",
    },
    {
      icon: Clock,
      title: "Timely Delivery",
      description:
        "We understand the importance of deadlines and strive to complete all projects on time without compromising quality.",
    },
    {
      icon: Users,
      title: "Expert Team",
      description:
        "Our team consists of licensed professionals with years of experience in various types of land surveying.",
    },
    {
      icon: Award,
      title: "Certified & Licensed",
      description:
        "Fully licensed by the Surveyors Council of Nigeria (SURCON) and compliant with all industry standards.",
    },
  ];

  return (
    <section className="py-20 bg-primary text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl" />
      </div>

      <div className="section-padding max-w-7xl mx-auto relative z-10">
        <AnimatedSection className="text-center mb-16">
          <span className="text-accent font-medium">Why Choose Us</span>
          <h2 className="heading-md mt-2 mb-4">The Bomtrack Advantage</h2>
          <p className="text-neutral-300 max-w-2xl mx-auto">
            We combine expertise, technology, and dedication to deliver
            surveying services that exceed expectations.
          </p>
        </AnimatedSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <AnimatedSection key={feature.title} delay={index * 0.1}>
              <div className="text-center">
                <div className="w-16 h-16 bg-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-lg font-semibold mb-3">{feature.title}</h3>
                <p className="text-neutral-300 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

// Dark CTA Section
function DarkCTASection() {
  return (
    <section className="py-20 bg-white">
      <div className="section-padding max-w-7xl mx-auto">
        <AnimatedSection>
          <div className="bg-primary rounded-3xl p-8 md:p-12 lg:p-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-96 h-96 bg-accent/30 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                  Your Trusted Partner in Land Surveying Projects
                </h2>
                <Link
                  to="/contact"
                  className="btn-primary inline-flex items-center gap-2 mt-4"
                >
                  Get in Touch
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>

              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl bg-white/5">
                  <img
                    src="/imgs/004.jpg"
                    alt="Surveying Equipment"
                    className="w-full h-[350px] object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 bg-accent text-white rounded-xl px-6 py-4 shadow-xl">
                  <div className="text-3xl font-bold">08+</div>
                  <div className="text-sm">Years Experience</div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// Features with Lady Section
function FeaturesWithLadySection() {
  const leftFeatures = [
    {
      icon: Award,
      title: "Proven Experience",
      desc: "In public and private sector projects",
    },
    {
      icon: Users,
      title: "Expert Technical Team",
      desc: "Highly qualified professionals",
    },
    {
      icon: Shield,
      title: "Versatile Solutions",
      desc: "Meeting diverse client needs",
    },
  ];

  const rightFeatures = [
    {
      icon: Clock,
      title: "Deadline Compliance",
      desc: "Respecting timelines and legal standards",
    },
    {
      icon: Target,
      title: "Social Commitment",
      desc: "Dedicated to community development",
    },
    {
      icon: Lightbulb,
      title: "Constant Innovation",
      desc: "Ensuring efficiency in all projects",
    },
  ];

  return (
    <section className="py-20 bg-neutral-50 relative overflow-hidden">
      <div className="section-padding max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-3 gap-8 items-center">
          <AnimatedSection className="space-y-8">
            {leftFeatures.map((feature) => (
              <div
                key={feature.title}
                className="flex items-start gap-4 text-right flex-row-reverse"
              >
                <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-neutral-600">{feature.desc}</p>
                </div>
              </div>
            ))}
          </AnimatedSection>

          <AnimatedSection delay={0.2} className="relative">
            <div className="relative mx-auto max-w-sm">
              <div className="absolute inset-0 bg-gradient-to-b from-accent/20 to-transparent rounded-full scale-110" />
              <img
                src="/keji.png"
                alt="Professional Surveyor"
                className="relative z-10 w-full h-auto object-cover rounded-2xl"
              />
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.3} className="space-y-8">
            {rightFeatures.map((feature) => (
              <div key={feature.title} className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-neutral-600">{feature.desc}</p>
                </div>
              </div>
            ))}
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

// CTA Section
function CTASection() {
  return (
    <section className="py-20 bg-white">
      <div className="section-padding max-w-7xl mx-auto">
        <AnimatedSection>
          <div className="bg-gradient-to-r from-primary to-primary-light rounded-3xl p-8 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-accent rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 text-center max-w-7xl mx-auto">
              <h2 className="heading-md text-white mb-6">
                Ready to Start Your Surveying Project?
              </h2>
              <p className="text-neutral-200 mb-8 leading-relaxed">
                Contact us today for a free consultation and quote. Our team is
                ready to help you with all your land surveying needs across
                Nigeria.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg font-medium transition-all duration-300 transform hover:scale-105"
                >
                  Get Free Quote
                </Link>
                <a
                  href="wa.me/+2348037400677"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-medium transition-all backdrop-blur-sm"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M12.04 2C6.58 2 2.14 6.44 2.14 11.9c0 1.75.46 3.45 1.34 4.96L2.05 22l5.28-1.39a9.86 9.86 0 0 0 4.71 1.2h.01c5.46 0 9.9-4.44 9.9-9.9C21.95 6.44 17.51 2 12.04 2Zm.01 18.13h-.01a8.23 8.23 0 0 1-4.19-1.15l-.3-.18-3.13.82.84-3.05-.2-.31a8.2 8.2 0 0 1-1.26-4.36c0-4.54 3.7-8.23 8.25-8.23a8.24 8.24 0 0 1 .01 16.46Zm4.52-6.17c-.25-.12-1.47-.72-1.69-.8-.23-.08-.39-.12-.56.12-.16.25-.64.8-.78.96-.14.17-.29.19-.54.07-.25-.13-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.15.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.08s.89 2.41 1.02 2.58c.12.17 1.76 2.69 4.27 3.77.6.26 1.06.41 1.42.52.6.19 1.14.16 1.57.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.11-.23-.17-.48-.29Z" />
                  </svg>
                  Call Us / Chat Us
                </a>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// Main Home Page
export default function Home() {
  return (
    <main className="min-h-screen">
      <Hero />
      <AboutSection />
      <ServicesSection />
      <FeaturesSection />
      <DarkCTASection />
      <FeaturesWithLadySection />
      <CTASection />
    </main>
  );
}
